import React from 'react';
import { Building2, CheckCircle, Trash2 } from 'lucide-react';
import { Ticket as TicketType, TicketResposta } from '@/types';
import Modal from '../ui/Modal';

interface TicketModalsProps {
  showTransferirModal: boolean;
  showFecharModal: boolean;
  showExcluirModal: boolean;
  showDeleteMessageModal: boolean;
  messageToDelete: TicketResposta | null;
  isTransferindo: boolean;
  isFechando: boolean;
  isExcluindo: boolean;
  isDeletingMessage: boolean;
  departamentos: Array<{id: number, nome: string, ativo: number}>;
  departamentoSelecionado: number;
  ticket: TicketType | null;
  onTransferir: () => void;
  onFechar: () => void;
  onExcluir: () => void;
  onDeleteMessage: () => void;
  onCloseTransferir: () => void;
  onCloseFechar: () => void;
  onCloseExcluir: () => void;
  onCloseDeleteMessage: () => void;
  onDepartamentoChange: (departamentoId: number) => void;
}

export default function TicketModals({
  showTransferirModal,
  showFecharModal,
  showExcluirModal,
  showDeleteMessageModal,
  messageToDelete,
  isTransferindo,
  isFechando,
  isExcluindo,
  isDeletingMessage,
  departamentos,
  departamentoSelecionado,
  ticket,
  onTransferir,
  onFechar,
  onExcluir,
  onDeleteMessage,
  onCloseTransferir,
  onCloseFechar,
  onCloseExcluir,
  onCloseDeleteMessage,
  onDepartamentoChange
}: TicketModalsProps) {
  return (
    <>
      {/* Modal de Confirmação para Transferir Ticket */}
      {showTransferirModal && (
        <Modal
          isOpen={showTransferirModal}
          onClose={onCloseTransferir}
          title={
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <Building2 className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Transferir Ticket</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Selecione o novo departamento</p>
              </div>
            </div>
          }
          maxWidth="md"
          closeOnBackdropClick={!isTransferindo}
        >
          <div className="p-6">
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Transferir o ticket <strong>#{ticket?.id}</strong> para outro departamento.
              </p>
              
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Departamento Atual
                </label>
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-900">
                    {ticket?.departamento_nome || 'Nenhum departamento'}
                  </span>
                </div>
                
                <label className="block text-sm font-medium text-gray-700">
                  Novo Departamento
                </label>
                <select
                  value={departamentoSelecionado}
                  onChange={(e) => onDepartamentoChange(Number(e.target.value))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  disabled={departamentos.length === 0}
                >
                  {departamentos.length === 0 ? (
                    <option value="">Nenhum departamento disponível</option>
                  ) : (
                    departamentos.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.nome}
                      </option>
                    ))
                  )}
                </select>
                
                {departamentos.length === 0 && (
                  <p className="text-sm text-orange-600">
                    Não há outros departamentos disponíveis para transferência.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onCloseTransferir}
                disabled={isTransferindo}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={onTransferir}
                disabled={isTransferindo || departamentos.length === 0}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isTransferindo ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Transferindo...
                  </div>
                ) : (
                  'Transferir Ticket'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Confirmação para Fechar Ticket */}
      {showFecharModal && (
        <Modal
          isOpen={showFecharModal}
          onClose={onCloseFechar}
          title={
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Fechar Ticket</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Confirmação necessária</p>
              </div>
            </div>
          }
          maxWidth="md"
          closeOnBackdropClick={!isFechando}
        >
          <div className="p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tem certeza que deseja fechar o ticket <strong>#{ticket?.id}</strong>?<br/>
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={onCloseFechar}
                disabled={isFechando}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={onFechar}
                disabled={isFechando}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isFechando ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Fechando...
                  </div>
                ) : (
                  'Sim, Fechar Ticket'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Confirmação para Excluir Ticket */}
      {showExcluirModal && (
        <Modal
          isOpen={showExcluirModal}
          onClose={onCloseExcluir}
          title={
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Excluir Ticket</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Confirmação necessária</p>
              </div>
            </div>
          }
          maxWidth="md"
          closeOnBackdropClick={!isExcluindo}
        >
          <div className="p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir o ticket <strong>#{ticket?.id}</strong>?<br/>
              <span className="text-red-600 dark:text-red-400 font-medium">Esta ação não pode ser desfeita!</span>
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={onCloseExcluir}
                disabled={isExcluindo}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={onExcluir}
                disabled={isExcluindo}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isExcluindo ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Excluindo...
                  </div>
                ) : (
                  'Sim, Excluir Ticket'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal de Confirmação para Excluir Mensagem */}
      {showDeleteMessageModal && messageToDelete && (
        <Modal
          isOpen={showDeleteMessageModal}
          onClose={onCloseDeleteMessage}
          title={
            <div className="flex items-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">Excluir Mensagem</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Confirmação necessária</p>
              </div>
            </div>
          }
          maxWidth="md"
          closeOnBackdropClick={!isDeletingMessage}
        >
          <div className="p-6">
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir esta mensagem?<br/>
              <span className="text-red-600 dark:text-red-400 font-medium">Esta ação não pode ser desfeita!</span>
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={onCloseDeleteMessage}
                disabled={isDeletingMessage}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={onDeleteMessage}
                disabled={isDeletingMessage}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isDeletingMessage ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Excluindo...
                  </div>
                ) : (
                  'Sim, Excluir'
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
